import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import CMSManager from './CMSManager';
import './admindashboard.css';

const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [dashboardData, setDashboardData] = useState({
    users: { total: 0, active: 0, new: 0 },
    news: { total: 0, published: 0, draft: 0 },
    contacts: { total: 0, unread: 0, today: 0 },
    system: { uptime: '0天', version: '1.0.0', status: 'running' }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 加载仪表板数据
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/dashboard');
      if (response.data.status === 'success') {
        const data = response.data.data;
        // 映射后端数据结构到前端期望的格式
        setDashboardData({
          users: {
            total: data.overview?.users?.total || 0,
            active: data.overview?.users?.active || 0,
            new: data.overview?.users?.admins || 0
          },
          news: {
            total: data.overview?.news?.total || 0,
            published: data.overview?.news?.published || 0,
            draft: data.overview?.news?.draft || 0
          },
          contacts: {
            total: data.overview?.contacts?.total || 0,
            unread: data.overview?.contacts?.pending || 0,
            today: data.overview?.contacts?.recentContacts || 0
          },
          system: {
            uptime: '运行中',
            version: '1.1.0',
            status: 'running'
          }
        });
      }
    } catch (error) {
      console.error('加载仪表板数据失败:', error);
      setError('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSection === 'overview') {
      loadDashboardData();
    }
  }, [activeSection]);

  // 渲染概览页面
  const renderOverview = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>加载中...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={loadDashboardData} className="btn btn-primary">
            重新加载
          </button>
        </div>
      );
    }

    return (
      <div className="overview-section">
        <h2>系统概览</h2>
        
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon user-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>用户管理</h3>
              <div className="stat-numbers">
                <div className="stat-item">
                  <span className="stat-value">{dashboardData.users.total}</span>
                  <span className="stat-label">总用户</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{dashboardData.users.active}</span>
                  <span className="stat-label">活跃用户</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{dashboardData.users.new}</span>
                  <span className="stat-label">新用户</span>
                </div>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon news-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9H9V9h10v2zm-4 4H9v-2h6v2zm4-8H9V5h10v2z"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>新闻管理</h3>
              <div className="stat-numbers">
                <div className="stat-item">
                  <span className="stat-value">{dashboardData.news.total}</span>
                  <span className="stat-label">总文章</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{dashboardData.news.published}</span>
                  <span className="stat-label">已发布</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{dashboardData.news.draft}</span>
                  <span className="stat-label">草稿</span>
                </div>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon contact-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>联系管理</h3>
              <div className="stat-numbers">
                <div className="stat-item">
                  <span className="stat-value">{dashboardData.contacts.total}</span>
                  <span className="stat-label">总联系</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{dashboardData.contacts.unread}</span>
                  <span className="stat-label">未读</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{dashboardData.contacts.today}</span>
                  <span className="stat-label">今日新增</span>
                </div>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon system-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>系统状态</h3>
              <div className="stat-numbers">
                <div className="stat-item">
                  <span className="stat-value">{dashboardData.system.uptime}</span>
                  <span className="stat-label">运行时间</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{dashboardData.system.version}</span>
                  <span className="stat-label">版本</span>
                </div>
                <div className="stat-item">
                  <span className={`stat-value status-${dashboardData.system.status}`}>
                    {dashboardData.system.status === 'running' ? '正常' : '异常'}
                  </span>
                  <span className="stat-label">状态</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="quick-actions">
          <h3>快速操作</h3>
          <div className="action-buttons">
            <button 
              onClick={() => setActiveSection('cms')}
              className="action-btn cms-btn"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
              </svg>
              <span>内容管理</span>
              <small>管理网站内容、配置和媒体文件</small>
            </button>
            
            <button 
              onClick={() => window.open('/api/admin/users', '_blank')}
              className="action-btn users-btn"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01 1l-2.54 3.51c-.28.39-.28.91 0 1.3l2.54 3.51c.47.63 1.21 1 2.01 1h1.54c.31 0 .62-.07.92-.2L20 22h-4z"/>
              </svg>
              <span>用户管理</span>
              <small>查看和管理系统用户</small>
            </button>
            
            <button 
              onClick={() => window.open('/api/admin/news/analytics', '_blank')}
              className="action-btn analytics-btn"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z"/>
              </svg>
              <span>数据分析</span>
              <small>查看网站访问和内容统计</small>
            </button>
            
            <button 
              onClick={() => loadDashboardData()}
              className="action-btn refresh-btn"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"/>
              </svg>
              <span>刷新数据</span>
              <small>重新加载最新的系统数据</small>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 渲染侧边栏
  const renderSidebar = () => {
    const menuItems = [
      {
        id: 'overview',
        label: '系统概览',
        icon: (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M13,3V9H21V3M13,21H21V11H13M3,21H11V15H3M3,13H11V3H3V13Z"/>
          </svg>
        )
      },
      {
        id: 'cms',
        label: '内容管理',
        icon: (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
          </svg>
        )
      },
      {
        id: 'users',
        label: '用户管理',
        icon: (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
        )
      },
      {
        id: 'analytics',
        label: '数据分析',
        icon: (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z"/>
          </svg>
        )
      }
    ];

    return (
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <h2>管理后台</h2>
        </div>
        <nav className="sidebar-nav">
          {menuItems.map(item => (
            <button
              key={item.id}
              className={`nav-item ${activeSection === item.id ? 'active' : ''}`}
              onClick={() => setActiveSection(item.id)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </nav>
      </div>
    );
  };

  // 渲染主要内容
  const renderMainContent = () => {
    switch (activeSection) {
      case 'overview':
        return renderOverview();
      case 'cms':
        return <CMSManager />;
      case 'users':
        return (
          <div className="placeholder-section">
            <h2>用户管理</h2>
            <p>用户管理功能正在开发中...</p>
          </div>
        );
      case 'analytics':
        return (
          <div className="placeholder-section">
            <h2>数据分析</h2>
            <p>数据分析功能正在开发中...</p>
          </div>
        );
      default:
        return renderOverview();
    }
  };

  return (
    <div className="admin-dashboard">
      {renderSidebar()}
      <div className="admin-main">
        <div className="main-content">
          {renderMainContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;